<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        sm="12"
        md="6"
        lg="6"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Uwagi do maszyn</span>
        <h3 class="page-title">Szczegóły uwagi do maszyny</h3>
      </d-col>
      <d-col
        sm="12"
        md="6"
        lg="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/maszyny/uwagi')">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
      </d-col>
    </d-row>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      Wczytywanie danych nie powiodło się.
      <a
        href="javascript:window.location.reload();"
        class="alert-link">
        Odśwież stronę
      </a>
      aby spróbować ponownie.
    </d-alert>

    <template v-else>
      <d-card class="mb-4">
        <transition name="fade">
          <div
            v-if="!loaded"
            class="overlay-loading" >
            <div class="loading-spinner pb-3 pt-3 text-center">
              <span class="loading-spinner-content text-primary">
                Trwa ładowanie danych&hellip;
              </span>
            </div>
          </div>
        </transition>
        <d-tabs
          card
          :class="{'is-loading': !loaded}">
          <!-- Dane ogólne -->
          <d-tab
            title="Dane ogólne"
            active
            class="px-3 px-md-4">
            <h4 class="mb-3 pb-3 border-bottom">
              Dane ogólne
            </h4>

            <d-row class="mb-3">
              <d-col md="4">
                <div class="form-group">
                  <label>
                    Maszyna:
                  </label>
                  <div class="text-primary">
                    {{ data.machine_name || '-' }}
                  </div>
                </div>
              </d-col>
              <d-col md="4">
                <div class="form-group">
                  <label>
                    Zamówienie:
                  </label>
                  <div class="text-primary">
                    {{ data.order_name || '-' }}
                  </div>
                </div>
              </d-col>
              <d-col md="4">
                <div class="form-group">
                  <label>
                    Zdobienie:
                  </label>
                  <div class="text-primary">
                    {{ data.order_service_name || '-' }}
                  </div>
                </div>
              </d-col>
            </d-row>

            <h5 class="mb-3 text-secondary">Uwagi</h5>

            <div class="note-card border rounded p-3 mb-3 bg-white shadow-sm">
              <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center border-bottom pb-2 mb-2">
                <div class="d-flex align-center">
                  <i
                    class="material-icons text-secondary mr-1"
                    style="font-size: 1.2rem;">person</i>
                  <strong class="text-primary">{{ data.user_name || '-' }}</strong>
                </div>
                <div class="d-flex align-center text-muted small">
                  <i
                    class="material-icons mr-1"
                    style="font-size: 1rem; top: unset;">schedule</i>
                  <span>{{ formatDateTimeString(data.created_at) }}</span>
                </div>
              </div>

              <p
                class="mb-0 mt-2"
                style="white-space: pre-wrap;">
                {{ data.comment || 'Brak treści uwagi.' }}
              </p>
            </div>
          </d-tab>

          <!-- Notatki tab -->
          <d-tab
            title="Notatki"
            class="px-3 px-md-4">
            <h4 class="mb-3 pb-3 border-bottom">
              Notatki do uwagi
            </h4>

            <div class="bg-light p-3 rounded mb-4 border">
              <d-row>
                <d-col cols="12">
                  <div class="form-group mb-3">
                    <label>Dodaj nową notatkę:</label>
                    <d-textarea
                      v-model="newNote"
                      :rows="3"
                      placeholder="Wpisz treść notatki…"
                      :disabled="addNoteInProgress" />
                  </div>
                </d-col>
                <d-col
                  cols="12"
                  class="text-right">
                  <d-button
                    theme="success"
                    :disabled="addNoteInProgress || !newNote"
                    @click.prevent="addNote">
                    <i
                      class="material-icons align-middle mr-1"
                      style="font-size: 1.2rem;">add</i>
                    Dodaj notatkę
                  </d-button>
                </d-col>
              </d-row>
            </div>

            <div class="notes-list-container">
              <h5 class="mb-3 text-secondary">Historia notatek</h5>

              <div
                v-if="!data.notes || data.notes.length === 0"
                class="text-center p-5 border rounded bg-light text-muted">
                Brak notatek do wyświetlenia&hellip;
              </div>

              <div v-else>
                <div
                  v-for="(note, index) in data.notes"
                  :key="'note-' + index"
                  class="note-card border rounded p-3 mb-3 bg-white shadow-sm">

                  <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center border-bottom pb-2 mb-2">
                    <div class="d-flex align-center">
                      <i
                        class="material-icons text-secondary mr-1"
                        style="font-size: 1.2rem;">person</i>
                      <strong class="text-primary">{{ note.user_name }}</strong>
                    </div>
                    <div class="d-flex align-center text-muted small">
                      <i
                        class="material-icons mr-1"
                        style="font-size: 1rem; top: unset;">schedule</i>
                      <span>{{ formatDateTimeString(note.created_at) }}</span>
                    </div>
                  </div>

                  <p
                    class="mb-0 mt-2"
                    style="white-space: pre-wrap;">
                    {{ note.note }}
                  </p>
                </div>
              </div>
            </div>
          </d-tab>
        </d-tabs>
      </d-card>
    </template>
  </d-container>
</template>

<script>
import FormUtils from '@/utils/FormUtils.js';
import CalendarUtils from '@/utils/CalendarUtils.js';

export default {
  name: 'machines-comment-form',
  data () {
    return {
      data: {
        id: 0,
        user_name: null,
        machine_name: null,
        order_name: null,
        order_service_name: null,
        comment: null,
        created_at: null,
        notes: []
      },
      loaded: false,
      loadError: false,
      newNote: '',
      addNoteInProgress: false
    };
  },
  mounted () {
    this.loadItemData();
  },
  methods: {
    loadItemData () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/api/machines/comments/item/',
        instanceFields: [
          'id',
          'comment',
          'user_name',
          'machine_name',
          'order_name',
          'order_service_name',
          'created_at',
          'notes'
        ],
        loadedKey: 'loaded',
        errorKey: 'loadError'
      });
    },
    addNote () {
      if (!this.newNote) {
        return;
      }

      this.addNoteInProgress = true;

      FormUtils.submit(this, {
        endpoint: '/api/machines/comments/add-note/' + this.id,
        formData: {
          note: this.newNote
        },
        successTitle: 'Dodano notatkę',
        successText: 'Notatka do uwagi została dodana',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Dodawanie notatki nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.newNote = '';
          this.addNoteInProgress = false;
          this.loadItemData();
        },
        failAction: () => {
          this.addNoteInProgress = false;
        }
      });
    },
    formatDateTimeString (date) {
      return CalendarUtils.formatDateTimeString(date);
    }
  }
};
</script>
